import { useMsal } from "@azure/msal-react";
import {
    DefaultButton, DirectionalHint, IColumn, IPanelProps, IRenderFunction, IStackProps, Panel,
    PanelType, PivotItem, PrimaryButton, Stack, Text, useTheme
} from "@fluentui/react";
import { RootState } from "app/store";
import ControlMask from "components/controls/ControlMask";
import TextFieldControl from "components/controls/TextFieldControl";
import ToggleControl from "components/controls/ToggleControl";
import { EControlType } from "components/ControlWrapper";
import { appStyles, createNonStandardColumns, friendlyValue, friendlyValueToFlexibleFieldValue, panelButtonsStyles } from "global/Application";
import { ESaveType, SaveAsync } from "global/Application/dataActions";
import { addUpdateNonStandards, INonStandardPayload } from "global/Reducer/dataSlice";
import { ParentVisibilityGraphProcessor } from "global/Visibility/ParentVisibilityGraph";
import { bcbs_config_masterattribute } from "model/AttributeOptionSets.type";
import {
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from 'model/CRM';
import {
    IAccountsReceivable as FAM_IAccountsReceivable,
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    IGroupConfigurationData as FAM_IGroupConfigurationData,
    INonStandard as FAM_INonStandard,
    INonStandardTemplate as FAM_INonStandardTemplate,
    IObjectWithNameId as FAM_IObjectWithNameId,
    TValue as FAM_TValue
} from 'model/FlexibleAttributeModel';
import React, { useEffect, useMemo, useState } from "react";
import { connect, ConnectedProps, useDispatch } from "react-redux";
import { ConfirmationDialog } from "../../components/controls/ConfirmationDialog";
import { IGridItem } from "../../components/controls/CustomDetailList/IGridItem";
import InformationCallout from "../../components/controls/InformationCallout";
import { TSearchPredicateAsync } from "../../components/controls/LookupControlDialog";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import { MsalTokenSource } from "../../global/DataSources/MsalTokenSource";
import { ArrayToLookupByPredicateWithMap } from "../../global/Utility/TSFunctions";
import FlexibleFieldView, { EScopeType } from "./FlexibleFieldView";
import NonStandardEditor from "./NonStandardEditor";
import { TChangeTracker } from "global/EmitEvent/Delta";
import { $att_$non_standard_$bcbs_benefitdefinition_id, $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id, $att_$non_standard_template_$bcbs_nst_benefitdefinition_id, $att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id } from "model/ConfigAttributeConstants";

export enum EVariant {
    panel, viewer
}

interface IProps extends TComponentConnectedProps {
    isOpen: boolean;
    onDismiss?: any;
    defaultValue: FAM_INonStandardTemplate | null;
    benefit?: FAM_IBenefit;
    accountsReceivable?: FAM_IAccountsReceivable;
    feesAndPremium?: FAM_IFeesAndPremiums;
    onChange?: (value: FAM_INonStandardTemplate, changeTracker: TChangeTracker) => void;
    costshareCategory?: IXrmTypes_bcbs_config_category;
    disabled?: boolean;
    variant?: EVariant;
    openViewer?: () => void;
}

interface IScopeSearchResult extends FAM_IObjectWithNameId {
    groupConfiguration: FAM_IGroupConfigurationData | null;
    benefit: FAM_IBenefit | null;
}
export interface INonstandardSearchResult extends FAM_IObjectWithNameId {
    item: IBenefitNonstandardScope | IGroupConfigurationNonstandardScope;
}

enum ENonstandardScope {
    GroupConfiguration,
    Benefit,
    AccountsReceivable,
    FeesAndPremiums
}

interface INonstandardScope {
    nonStandard: FAM_INonStandard;
    parent: FAM_IBenefit | FAM_IGroupConfigurationData | FAM_IAccountsReceivable | FAM_IFeesAndPremiums;
    type: ENonstandardScope;
}

interface IBenefitNonstandardScope extends INonstandardScope {
    parent: FAM_IBenefit
    type: ENonstandardScope.Benefit;
}
interface IGroupConfigurationNonstandardScope extends INonstandardScope {
    parent: FAM_IGroupConfigurationData;
    type: ENonstandardScope.GroupConfiguration;
}

interface IImportNonstandardContext {
    snapshot?: IXrmTypes_bcbs_groupconfigurationsnapshot | null;
    json?: FAM_IGroupConfigurationData | null;
    nonStandardSearchResult?: INonstandardScope[] | null;
    scopeSearchResult?: IScopeSearchResult[] | null;
    searching?: boolean;
}

const NonStandardTemplateEditor: React.FC<IProps> = (props) => {
    const [nonStandardTemplate, setNonStandardTemplate] = useState<FAM_INonStandardTemplate | null>(null);
    const [wasPreviouslyDeactivated, setWasPreviouslyDeactivated] = useState(false);
    const [changeTracker, setChangeTracker] = useState<TChangeTracker>({});

    const [nonStandardLinkIsMounted, setNonStandardLinkIsMounted] = useState(false);
    const [nonStandardLinkIsOpen, _setNonStandardLinkIsOpen] = useState(false);
    const setNonStandardLinkIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!nonStandardLinkIsMounted)
            setNonStandardLinkIsMounted(true);
        _setNonStandardLinkIsOpen(value);
    }

    const [alertIsMounted, setAlertIsMounted] = useState(false);
    const [alertIsOpen, _setAlertIsOpen] = useState(false);
    const setAlertIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!alertIsMounted)
            setAlertIsMounted(true);
        _setAlertIsOpen(value);
    }

    const [sourceContext, setSourceContext] = useState<IImportNonstandardContext | null>({
        nonStandardSearchResult: null,
        scopeSearchResult: null,
        snapshot: null,
        json: null,
    });

    const parentVisibilityProcessor = useMemo(() => {
        const gProcessor = new ParentVisibilityGraphProcessor(
            props.state.data.config,
            props.state.data.config.cache.parentVisibilityGraph
        );

        return gProcessor;
    }, []);

    // allows the component to perform a local visibility management, to help with the animation and ui smoothness
    // by default this should always be visible unless components has signaled a context job termination and wants to self close
    const [selfVisible, setSelfVisible] = useState(true);

    const categoryNonStandardTemplate = props.state.data.config.cache.map_bcbs_config_category_named['Non-Standard Template'];
    const theme = useTheme();
    const dispatch = useDispatch();
    const state = props.state;
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    useEffect(() => {
        if (props.isOpen) {
            setSelfVisible(true);

            setSourceContext(q => {
                return {
                    json: null,
                    nonStandardSearchResult: null,
                    scopeSearchResult: null,
                    snapshot: null
                }
            });

            setChangeTracker({});
            if (props.defaultValue)
            {
                setNonStandardTemplate(props.defaultValue);
                setWasPreviouslyDeactivated(props.defaultValue.deactivated)
            }
        }

    }, [props.isOpen, props.defaultValue]);

    const onRenderProperties: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {
        if (!nonStandardTemplate)
            return <></>

        const isBulk = nonStandardTemplate?.id === 'bulk';

        const displayAddButtons = (nonStandardTemplate.linkedCategoryTypeId && nonStandardTemplate.relationships && nonStandardTemplate.relationships.length > 0);

        return <PivotItem
            headerText={"Properties"}
            key={"nonStandardTemplateProperties"}
        >
            <Stack {...columnProps}>

                <TextFieldControl
                    label={"Name"}
                    disabled={isBulk || props.disabled}
                    fieldValueOnChange={(event, value) => {
                        setNonStandardTemplate(t => {
                            return {
                                ...t!,
                                name: value.value ?? ''
                            };
                        })
                    }}
                    defaultFieldValue={{ type: 'string', value: nonStandardTemplate.name ?? '' }}
                />
                <ControlMask
                    label="Deactivate"
                    defaultLocked={isBulk}
                    onUnlock={() => {
                        setNonStandardTemplate(t => { return { ...t!, deactivated: false }; });
                    }}
                >
                    <ToggleControl
                        label={"Deactivate"}
                        checked={nonStandardTemplate.deactivated ?? false}
                        disabled={props.disabled}
                        onChange={(_, checked) => {
                            setNonStandardTemplate(t => { return { ...t!, deactivated: checked ?? false }; });
                        }}
                    />
                </ControlMask>

                {displayAddButtons &&
                    <div>
                        {/* <PrimaryButton
                        text='Add Non-Standards'
                        styles={panelButtonsStyles}
                        onClick={() => {
                            setSourceContext({
                                snapshot: props.state.data.snapshot,
                                json: props.state.data.json,
                                scopeSearchResult: null,
                            });
                            setNonStandardLinkIsOpen(true);
                        }}
                        /> */}
                        <PrimaryButton
                            text='Add Template To New Benefit'
                            styles={panelButtonsStyles}
                            onClick={() => {
                                if (props.openViewer) {
                                    props.openViewer!();
                                }
                            }}
                        />
                    </div>
                }
            </Stack>

        </PivotItem>
    }

    const columnProps: Partial<IStackProps> = useMemo(() => {
        return {
            tokens: { childrenGap: 15 },
            styles: {
                root: {
                    boxShadow: theme.effects.elevation16,
                    padding: '20px',
                    margin: '20px 0px 0px 0px',
                    backgroundColor: theme.semanticColors.menuBackground
                },
            },
        };
    }, [theme]);

    function updateAllTemplatedNonStandardsFields(incomingTemplate: FAM_INonStandardTemplate)
    {
        //read template fields
        //clone the template
        const template = structuredClone(incomingTemplate);

        const templateFields = ArrayToLookupByPredicateWithMap(template!.fields, t => t.configRef.id, t => {
            const attribute = props.state.data.config.cache.map_bcbs_config_attribute[t.configRef.id];
            const masterAttribute = props.state.data.config.cache.map_bcbs_config_masterattribute[attribute._bcbs_config_masterattributeid_value!];
            const controlType = masterAttribute.bcbs_attributetypecode as EControlType;
            return {
                field: t,
                attribute,

                masterAttribute,
                controlType,
            }
        });
        const templateDefinition = templateFields[$att_$non_standard_template_$bcbs_nst_benefitdefinition_id];
        const templatePayAccordingToBenefit = templateFields[$att_$non_standard_template_$bcbs_nst_doesthisbenefitpayaccoridngtotherelatedbe_id];
        let templateDefinitionValue = "";
        let templatePayAccordingToBenefitValue = "";

        if (templateDefinition.field.values[0].value !== null) {
            templateDefinitionValue = friendlyValue(templateDefinition.controlType, templateDefinition.field.values[0].value);
        }
        if (templatePayAccordingToBenefit.field.values[0].value !== null) {
            templatePayAccordingToBenefitValue = friendlyValue(templatePayAccordingToBenefit.controlType, templatePayAccordingToBenefit.field.values[0].value);
        }
        
        let nonStandardPayloads: INonStandardPayload[] = []
        
        // Helper function to update non-standard fields and create payload
        const updateNonStandardAndCreatePayload = (
            nonStandard: FAM_INonStandard,
            benefit?: FAM_IBenefit,
            groupConfiguration?: FAM_IGroupConfigurationData,
            accountsReceivable?: FAM_IAccountsReceivable,
            feesAndPremium?: FAM_IFeesAndPremiums
        ) => {
            const updatedNonStandard: FAM_INonStandard = structuredClone(nonStandard);
            updatedNonStandard.name = template.name;
            updatedNonStandard.partOfTemplate = true;
            
            let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_benefitdefinition_id);
            let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === $att_$non_standard_$bcbs_doesthisbenefitpayaccoridngtotherelatedbe_id);

            const benefitDefinitionValue = templateDefinition.field.values[0].value;
            nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue);

            if (nonStandardPayAccordingToBenefit) {
                const payAccordingToBenefitValue = templatePayAccordingToBenefit.field.values[0].value;
                nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue);
            }

            const nonStandardPayload: INonStandardPayload = {
                config: props.state.data.config,
                nonStandards: [updatedNonStandard],
                benefit: benefit,
                groupConfiguration: groupConfiguration,
                accountsReceivable: accountsReceivable,
                feesAndPremium: feesAndPremium,
                changeTracker: {}
            };
            nonStandardPayloads.push(nonStandardPayload);
        };

        //go through each connected NS and update the fields on that.
        template?.relationships?.forEach(rel =>
        {
            let categoryId = rel.categoryID;
            rel.sectionRefs?.forEach(sectionRef =>
            {
                sectionRef.nonStandards?.forEach(nonStandardRef =>
                {
                    // Check benefits
                    let benefit = props.state.data.json.benefits.find(ben => ben.id === categoryId);
                    if (benefit) {
                        let benefitNonStandard = benefit.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (benefitNonStandard && benefitNonStandard.deactivated !== true) {
                            updateNonStandardAndCreatePayload(benefitNonStandard, benefit);
                            return;
                        }
                    }
                    
                    // Check group configuration
                    if (props.state.data.json.id === categoryId) {
                        let gcNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (gcNonStandard && gcNonStandard.deactivated !== true) {
                            updateNonStandardAndCreatePayload(gcNonStandard, undefined, props.state.data.json);
                            return;
                        }
                    }

                    // Check accounts receivable
                    let ar = props.state.data.json.claimsARs?.find(a => a.id === categoryId);
                    if (ar) {
                        let arNonStandard = ar.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (arNonStandard && arNonStandard.deactivated !== true) {
                            updateNonStandardAndCreatePayload(arNonStandard, undefined, undefined, ar);
                            return;
                        }
                    }

                    // Check fees and premiums
                    let fp = props.state.data.json.feesAndPremiums?.find(f => f.id === categoryId);
                    if (fp) {
                        let fpNonStandard = fp.nonStandards.find(ns => ns.id === nonStandardRef.id);
                        if (fpNonStandard && fpNonStandard.deactivated !== true) {
                            updateNonStandardAndCreatePayload(fpNonStandard, undefined, undefined, undefined, fp);
                            return;
                        }
                    }
                })
            })
        });
        if (nonStandardPayloads.length > 0){
            dispatch(addUpdateNonStandards(nonStandardPayloads))
        }
    }

    

    const onRenderBody: IRenderFunction<IPanelProps> = (bodyProps, defaultRender) => {

        if (!props.defaultValue)
            return <div>unexpected state</div>

        return <FlexibleFieldView
            fields={nonStandardTemplate?.fields! ?? []}
            category={categoryNonStandardTemplate}
            tiers={[]}
            scopeType={EScopeType.nonStandardTemplate}
            lastPivotItem={[onRenderProperties]}
            onChange={(changeEvent) => {
                if (changeEvent.field) {

                    setChangeTracker(xChange => {
                        const nChange: typeof xChange = { ...xChange };
                        nChange[changeEvent.field?.configRef.id!] = {
                            field: changeEvent.field!,
                            fieldValue: changeEvent.fieldValue!,
                            value: changeEvent.value!,
                        }
                        return nChange;
                    });

                    setNonStandardTemplate(xNonStandardTemplate => {
                        const nNonStandardTemplate = { ...xNonStandardTemplate! };

                        const fields = nNonStandardTemplate.fields;
                        const field = nNonStandardTemplate?.fields?.find(q => q.id === changeEvent.field!.id);

                        if (field) {
                            const fieldValue = field?.values?.find(q => q.id === changeEvent.fieldValue!.id);

                            if (fieldValue)
                                fieldValue.value = changeEvent.value!;

                            const graphChanges = parentVisibilityProcessor.processVisibilityOnAttributeChange(
                                props.state.data.config,
                                field,
                                fields
                            );

                            // TODO refactor this to a helper function so you can call the second iteration with a TTL to avoid infinite loops 
                            graphChanges.forEach(eGraphChange => {
                                if (fields)
                                    if (eGraphChange.newVisibility === 0) {

                                        delete fields[eGraphChange.index].visibility;

                                        // TODO refactor, unify all three and clean up
                                        // * Duplicated code, this has been duplicated in addUpdateAttributeSet, AttributeSetGridViewEditor.onChange, NonStandardGridViewEditor.onChange
                                        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/319161
                                        fields[eGraphChange.index].values.forEach(eachValue => {

                                            if (eGraphChange.attribute.bcbs_defaultvalue)
                                                switch (eGraphChange.masterAttribute.bcbs_attributetypecode) {

                                                    // ignore, nothing to do, since it is of type message
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Message(808900014)'].value:
                                                        break;

                                                    // ignore, nothing to do, since it is of type message
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Lookup(808900006)'].value:
                                                        throw new Error("Not implemented exception!")
                                                        break;

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Single Line of Text(808900008)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Boolean(808900000)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Option Set(808900007)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Currency(808900002)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Date(808900003)'].value:

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Whole Number(808900010)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Decimal(808900004)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Percentage(808900005)'].value:

                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Costshare(808900001)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Percentage(808900005)'].value:
                                                    case bcbs_config_masterattribute.bcbs_attributetypecode['Vision Costshare(808900012)'].value:

                                                        // blank string or no-value at all
                                                        if (
                                                            !eachValue.value ||
                                                            (eachValue.value.type === 'string' && eachValue.value.value === '')
                                                        ) {

                                                            const field = fields![eGraphChange.index];
                                                            const fieldValue = eachValue;

                                                            const value: FAM_TValue = friendlyValueToFlexibleFieldValue(
                                                                props.state.data.config,
                                                                eGraphChange.attribute.bcbs_defaultvalue!,
                                                                eGraphChange.attribute,
                                                                eGraphChange.masterAttribute,
                                                                null
                                                            );

                                                            setChangeTracker(xChange => {
                                                                const nChange: typeof xChange = { ...xChange };
                                                                nChange[field.configRef.id] = {
                                                                    field: field,
                                                                    fieldValue: structuredClone(fieldValue),
                                                                    value: value!,
                                                                }
                                                                return nChange;
                                                            });

                                                            eachValue.value = value
                                                        }

                                                        break;

                                                    default:
                                                        break;
                                                }
                                        })
                                    }
                                    else {

                                        fields[eGraphChange.index].visibility = eGraphChange.newVisibility;
                                    }
                            });

                        }

                        return nNonStandardTemplate;
                    });
                }
            }}
        />
    }

    const onChange = (value: FAM_INonStandardTemplate, changeTracker: TChangeTracker) => {
        props.onChange?.(value, changeTracker);
    }

    /**
    * Search for all available non-standards inside a snapshot and output list of scopes
    * Group Configuration, Benefit 1, Benefit 2, ...
    * @param value 
    * @returns 
    */
    const onSearchScopes: TSearchPredicateAsync<IScopeSearchResult> = async (value) => {
        const searchValue = value.toLowerCase() ?? '';

        if (!sourceContext?.snapshot || !sourceContext.json)
            throw new Error('expecting snapshot to search');

        return new Promise((resolve, reject) => {

            let json = sourceContext.json;

            let items: IGridItem<IScopeSearchResult>[] = [];

            if (!json)
                reject(new Error('unexpected'));

            if (nonStandardTemplate?.linkedCategoryName === "Group Configuration") {
                if ((json?.nonStandards?.length ?? 0) > 0) {
                    let add = false;

                    const search = 'group configuration'.toLowerCase();

                    // if we are not searching for anything, return as/is
                    if (!searchValue)
                        add = true;

                    // lets look into search
                    if (search.indexOf(searchValue) > -1)
                        add = true;

                    if (add)
                        items.push({
                            key: '-1',
                            _index: -1,
                            scope: 'Group Configuration',
                            name: 'Group Configuration',
                            search: 'group configuration',
                            total: json?.nonStandards?.length ?? 0,
                            data: {
                                id: json!.id,
                                name: json!.name,
                                groupConfiguration: json ?? null,
                                benefit: null
                            }
                        });
                }
            }

            json?.benefits?.forEach((benefit, index) => {
                let add = false;
                let relationshipExists = false;
                const scope = `Benefits (${benefit.bcbs_name})/${benefit.name}`;
                const search = scope.toLowerCase();

                // if we are not searching for anything, return as/is
                if (!searchValue)
                    add = true;

                // lets look into search
                if (scope && search.indexOf(searchValue) > -1)
                    add = true;

                //go through benefits to make sure they are already on the template
                nonStandardTemplate?.relationships?.forEach(rel => {
                    if (rel.categoryID === benefit.id)
                        relationshipExists = true;
                })

                let currentBenefitCategory = props.state.data.config.cache.map_bcbs_config_category[benefit.bcbs_config_categoryid!];
                if ((add && nonStandardTemplate?.linkedCategoryName === currentBenefitCategory.bcbs_name) && relationshipExists)
                    if (benefit?.nonStandards?.length > 0)
                        items.push({
                            key: index,
                            _index: index,
                            scope: scope,
                            search: search,
                            name: scope,
                            total: benefit?.nonStandards?.length ?? 0,
                            data: {
                                id: benefit.id,
                                name: benefit.name,
                                groupConfiguration: null,
                                benefit: benefit
                            }
                        });
            });

            resolve({
                columns: [{
                    key: 'scope',
                    minWidth: 550,
                    name: 'Entity',
                    fieldName: 'scope',
                    isResizable: true,
                },
                {
                    key: 'total',
                    minWidth: 50,
                    name: 'Count',
                    fieldName: 'total',
                    isResizable: true,
                }],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,
            });
        });
    }

    /**
     * Search for all records applicable to selected scope
     * @param value 
     * @returns 
     */
    const onSearchNonStandards: TSearchPredicateAsync<INonstandardSearchResult> = async (value) => {
        const searchValue = value?.toLowerCase() ?? '';

        return new Promise((resolve, reject) => {

            const items: IGridItem<INonstandardSearchResult>[] = [];

            sourceContext?.nonStandardSearchResult?.forEach((nonStandardItem, ix) => {

                let add = false;

                var nonStandardOnTemplate = false;

                nonStandardTemplate?.relationships?.forEach(rel => {
                    rel.sectionRefs?.forEach(sec => {
                        var ns = sec.nonStandards?.find(ns => ns.id === nonStandardItem.nonStandard.id);
                        if (ns) {
                            nonStandardOnTemplate = true;
                        }
                    })
                });

                if (!add && !searchValue && !nonStandardOnTemplate)
                    add = true;

                if (!add && nonStandardItem.nonStandard.name.toLowerCase().indexOf(searchValue) > -1 && !nonStandardOnTemplate)
                    add = true;

                if (add && !nonStandardItem.nonStandard.partOfTemplate) {//make sure the non standard is not already part of a template

                    const item: IGridItem<INonstandardSearchResult> = {
                        key: ix,
                        _index: ix,
                        name: nonStandardItem.nonStandard.name,
                        scope: nonStandardItem.parent.name,
                        data: {
                            name: nonStandardItem.nonStandard.name,
                            id: nonStandardItem.nonStandard.id,
                            item: nonStandardItem as IBenefitNonstandardScope | IGroupConfigurationNonstandardScope,
                        }
                    };


                    nonStandardItem.nonStandard.fields.forEach(eaField => {
                        const configAttributeId = eaField.configRef.id;
                        eaField.values.forEach(eachFieldValue => {
                            item[`${configAttributeId}-${eachFieldValue?.tierRef?.id ?? ''}`] = eachFieldValue.value;
                        });
                    });

                    items.push(item);

                }
            })

            const quickViewColumn: IColumn = {
                key: 'view',
                minWidth: 50,
                name: 'View',
                fieldName: 'view',
                isResizable: true,
                onRender(item: IGridItem<INonstandardSearchResult>, index, column) {
                    return onRenderInformationCallout(item)
                },
            };


            const tempSection = props.state.data.config.cache.map_bcbs_config_section[nonStandardTemplate?.relationships![0].sectionRefs![0].sectionId!];
            // generating a temporary non-standard to match the schema
            const columns = createNonStandardColumns(
                props.state.data.config,
                {
                    section: tempSection,
                    //config: props.state.data.config,
                    tierNames: null
                },
                undefined,
                undefined,
                (field, configAttribute, configMasterAttribute) => {

                    // https://bcbsonline.crm.dynamics.com/main.aspx?appid=be2013bc-a215-ed11-b83c-00224820a835&forceUCI=1&pagetype=entityrecord&etn=bcbs_config_attribute&id=ff72f3f2-d38d-ec11-b400-0022480a9a56
                    if (configAttribute?.bcbs_config_attributeid === $att_$non_standard_$bcbs_benefitdefinition_id)
                        return true;

                    return false;
                }
            );


            resolve({
                columns: [
                    {
                        key: 'name',
                        minWidth: 350,
                        name: 'Name',
                        fieldName: 'name',
                        isResizable: true,
                    },
                    {
                        key: 'scope',
                        minWidth: 300,
                        name: 'Entity',
                        fieldName: 'scope',
                        isResizable: true,
                    },
                    ...columns,
                    quickViewColumn,
                ],
                items: items,
                showNoResultPrompt: items.length === 0,
                showLimitedResultPrompt: false,

            })
        });
    }

    // const onRenderLinkNonStandards = () => {
    //     if (!nonStandardLinkIsMounted)
    //         return <></>;

    //     return <div className={fadeIn}>
    //             <Stack tokens={{ childrenGap: 10 }}>

    //                 {sourceContext?.searching &&
    //                     <PageLoading />
    //                 }

    //                 <LookupControlBase
    //                     label='Entity'
    //                     value={sourceContext?.scopeSearchResult?.[0]?.name}
    //                     disabled
    //                     onLookup={() => {
    //                         setSourceContext(q => {
    //                             return {
    //                                 json: null,
    //                                 nonStandardSearchResult: null,
    //                                 scopeSearchResult: null,
    //                                 snapshot: null
    //                             }
    //                         });
    //                     }}
    //                 />

    //                 <LookupControlDialog
    //                 isOpen={sourceContext?.snapshot !== null &&
    //                     sourceContext?.json !== null &&
    //                     sourceContext?.scopeSearchResult === null}
    //                     label={'Entity'}
    //                     onSearch={onSearchScopes}
    //                     variant={"single"}
    //                     onPick={(picks) => {
    //                     const nonStandards: INonstandardScope[] = [];

    //                     // nothing to do
    //                     if (!picks || picks.length < 1) {
    //                         setSourceContext(q => {
    //                             return {
    //                                 json: null,
    //                                 nonStandardSearchResult: null,
    //                                 scopeSearchResult: null,
    //                                 snapshot: null
    //                             }
    //                         });
    //                         return;
    //                     }

    //                     // Group Configuration Scope:
    //                     // --------------------------------------
    //                     const gcNS = picks.find(q => sourceContext?.json === q.groupConfiguration);

    //                     if (gcNS && sourceContext?.json?.nonStandards)
    //                         sourceContext?.json?.nonStandards.forEach(t => {

    //                             if (t.deactivated)
    //                                 return;

    //                             nonStandards.push({
    //                                 type: ENonstandardScope.GroupConfiguration,
    //                                 parent: sourceContext.json!,
    //                                 nonStandard: t,
    //                             });
    //                         });

    //                     // Benefits Scope Scope:
    //                     // --------------------------------------
    //                     sourceContext?.json?.benefits.forEach(t => {

    //                         if (t.deactivated)
    //                             return;

    //                         const picked = picks.find(q => q.benefit === t);

    //                         if (picked) {
    //                             t.nonStandards.forEach(q => {

    //                                 if (q.deactivated)
    //                                     return;

    //                                 nonStandards.push({
    //                                     type: ENonstandardScope.Benefit,
    //                                     parent: t,
    //                                     nonStandard: q,
    //                                 });
    //                             });
    //                         }

    //                     });

    //                     setSourceContext(q => {
    //                         return {
    //                             ...q,
    //                             nonStandardSearchResult: nonStandards,
    //                             scopeSearchResult: picks,
    //                         }
    //                     });

    //                 }}
    //                 onCancel={() => {
    //                     setSourceContext(q => {
    //                         return {
    //                             ...q,
    //                             snapshot: null,
    //                             nonStandardSearchResult: null,
    //                             scopeSearchResult: null,
    //                         }
    //                     });
    //                     setNonStandardLinkIsOpen(false);
    //                 }}
    //                 onDismiss={() => {
    //                     setSourceContext(q => {
    //                         return {
    //                             ...q,
    //                             snapshot: null,
    //                             nonStandardSearchResult: null,
    //                             scopeSearchResult: null,
    //                         }
    //                     });
    //                     setNonStandardLinkIsOpen(false);
    //                 }}

    //             />

    //             <LookupControlDialog
    //                 isOpen={!!sourceContext?.nonStandardSearchResult}
    //                 label={'Non-Standards'}
    //                 variant="multiple"
    //                 onSearch={onSearchNonStandards}
    //                 onPick={(picks) => {
                        
    //                     dispatch(addNonStandardToTemplateFromTemplate(picks, {nonStandardTemplate: nonStandardTemplate, benefit: props.benefit, groupConfiguration: props.state.data.json}));
    //                     // const nonStandardTemplates: FAM.INonStandardTemplate[] = [];

    //                     // let newRelationshipList = nonStandardTemplate!.relationships!.map(rel => ({
    //                     //     ...rel,
    //                     //     sectionRefs: (rel.sectionRefs!.map(secRef => ({
    //                     //         ...secRef,
    //                     //         nonstandards: [...(secRef.nonstandards || [])] // Clone the nonstandards array
    //                     //     })) || [])
    //                     // }));

    //                     // //Link Non-Standard from Template Side
    //                     // picks?.forEach(pick => {
    //                     //     //we are adding a non standard from template side
    //                     //     if (!nonStandardTemplate) { return; }

    //                     //     const fieldMap = ArrayToLookupByPredicate(nonStandardTemplate.fields, q => q.configRef.id);
    //                     //     const linkedConfigCategory = props.state.data.config.cache.map_bcbs_config_category[nonStandardTemplate?.linkedCategoryTypeId!];

    //                     //     // lets create a new nonstandard template as
    //                     //     let template: FAM.INonStandardTemplate = createNonStandardTemplate(
    //                     //         props.state.data,
    //                     //         {
    //                     //             id: "",
    //                     //             name: `Non-StandardTemplate - New - ${timestamp()}`,
    //                     //             category: categoryNonStandardTemplate,
    //                     //             linkedCategory: linkedConfigCategory ?? null,
    //                     //             linkedCategoryName: nonStandardTemplate.linkedCategoryName ?? null,
    //                     //             relationships: nonStandardTemplate.relationships ?? []
    //                     //         }
    //                     //     );

    //                     //     let tierChanges: ITierLevelChange[] = [];

    //                     //     // transfer matching properties 
    //                     //     template.name = nonStandardTemplate.name;
    //                     //     template.id = nonStandardTemplate.id;

    //                     //     //get the pick category type
    //                     //     var pickSpecificCategoryId = pick.item.parent.id;
                            
    //                     //     // Find the category in the template to update
    //                     //     let category = newRelationshipList.find(cat => cat.categoryID === pickSpecificCategoryId);
    //                     //     const clonedNS: FAM.INonStandard = { ...pick.item.nonStandard, partOfTemplate: true }
    //                     //     if (category) {
    //                     //         // Find or create the section within the category
    //                     //         let section = category.sectionRefs.find(sec => sec.sectionId === pick.item.nonStandard.sectionRef?.id);

    //                     //         if (section) {
    //                     //             // Section exists, add the non-standard to it
    //                     //             section.nonstandards = [...section.nonstandards, { id: clonedNS.id, name: clonedNS.name }];
    //                     //         } else {
    //                     //             // Section doesn't exist, create it and add the non-standard
    //                     //             const newSectionRef = {
    //                     //                 sectionId: pick.item.nonStandard.sectionRef?.id || '',
    //                     //                 nonstandards: [{ id: clonedNS.id, name: clonedNS.name }]
    //                     //             };
    //                     //             category.sectionRefs = [...(category.sectionRefs || []), newSectionRef];
    //                     //         }
    //                     //     } else {
    //                     //         // Category doesn't exist, create a new category with the section and non-standard
    //                     //         var newSection = props.state.data.config.cache.map_bcbs_config_section[pick.item.nonStandard.sectionRef!.id];
    //                     //         let categoryID = newSection!._bcbs_config_categoryid_value!;
    //                     //         let newCat = props.state.data.config.cache.map_bcbs_config_category[categoryID]
    //                     //         const newCategory = {
    //                     //             categoryID: pickSpecificCategoryId ?? "",
    //                     //             categoryType: newCat?.bcbs_name ?? "",
    //                     //             sectionRefs: [{
    //                     //                 sectionId: pick.item.nonStandard.sectionRef?.id || '',
    //                     //                 nonstandards: [structuredClone(pick.item.nonStandard)]
    //                     //             }]
    //                     //         };
    //                     //         newRelationshipList = [...newRelationshipList, newCategory];
    //                     //     }

    //                     //     //need to add the new nonstandard to every relationship other than the one we just added to
    //                     //     newRelationshipList.forEach(cat => {
    //                     //         if (cat.categoryID !== pickSpecificCategoryId) {
    //                     //             //send the linkedNS to matching. determine if there is a match and return a NONStandard then do conditional stuff
    //                     //             let matchingNonStandardIds = matchingNonStandard(pick.item.nonStandard, template, cat);

    //                     //             //if we have ids in the list, we can skip this creation of new ones
    //                     //             if (matchingNonStandardIds && matchingNonStandardIds.length > 0) {
    //                     //                 matchingNonStandardIds.forEach(id => {
    //                     //                     let benefit = props.state.data.json.benefits.find(q => q.id === cat.categoryID);
    //                     //                     //get the nonstandard from the GC or Benefit
    //                     //                     let categoryNonStandard = benefit ? benefit.nonStandards.find(q => q.id === id) : props.state.data.json.nonStandards.find(q => q.id === id);
    //                     //                     if (categoryNonStandard) {
    //                     //                         //update the matched nonstandards for this category
    //                     //                         const updatedNonStandard: FAM.INonStandard = structuredClone(categoryNonStandard);
    //                     //                         updatedNonStandard.name = template.name;
    //                     //                         updatedNonStandard.partOfTemplate = true;

    //                     //                         let nonStandardBenefitDefintion = updatedNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
    //                     //                         let nonStandardPayAccordingToBenefit = updatedNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

    //                     //                         let templateDefintion = fieldMap["1fe66c74-710c-ef11-9f89-6045bdeb64f8"];

    //                     //                         const benefitDefinitionValue = templateDefintion.values[0].value; //template value
    //                     //                         nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

    //                     //                         if (nonStandardPayAccordingToBenefit) {
    //                     //                             let templatePayAccordingToBenefit = fieldMap["05beb613-720c-ef11-9f89-6045bdeb64f8"];
    //                     //                             const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
    //                     //                             nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
    //                     //                         }

    //                     //                         // Find or create the section within the category
    //                     //                         let section = cat.sectionRefs.find(sec => sec.sectionId === pick.item.nonStandard.sectionRef?.id);

    //                     //                         if (section) {
    //                     //                             // Section exists, add the non-standard to it
    //                     //                             section.nonstandards = [...section.nonstandards, { id: updatedNonStandard.id, name: updatedNonStandard.name }];
    //                     //                         } else {
    //                     //                             // Section doesn't exist, create it and add the non-standard
    //                     //                             const newSectionRef = {
    //                     //                                 sectionId: pick.item.nonStandard.sectionRef?.id || '',
    //                     //                                 nonstandards: [{ id: updatedNonStandard.id, name: updatedNonStandard.name }]
    //                     //                             };
    //                     //                             cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
    //                     //                         }

    //                     //                         dispatch(addUpdateNonStandards([{
    //                     //                             config: props.state.data.config,
    //                     //                             nonStandards: [updatedNonStandard],
    //                     //                             benefit: benefit,
    //                     //                             //groupConfiguration: props.groupConfiguration,
    //                     //                             changeTracker: {}
    //                     //                         }]));
    //                     //                     }
    //                     //                 })

    //                     //             } else {
    //                     //                 let newNonStandardSection = props.state.data.config.cache.map_bcbs_config_section[pick.item.nonStandard.sectionRef!.id];
    //                     //                 let benefit = props.state.data.json.benefits.find(ben => (ben.id === cat.categoryID));
    //                     //                 const newNonStandard: FAM.INonStandard = createNonStandard(
    //                     //                     props.state.data,
    //                     //                     {
    //                     //                         section: newNonStandardSection!,
    //                     //                         benefit: benefit,
    //                     //                         //groupConfiguration: props.groupConfiguration,
    //                     //                         templated: true,
    //                     //                         name: nonStandardTemplate.name
    //                     //                     }
    //                     //                 );
    //                     //                 const fieldMap = ArrayToLookupByPredicate(nonStandardTemplate.fields, q => q.configRef.id);
    //                     //                 //this newly created one needs to have the fields from tempalte - for some reason these ones arent caught in the wide update
    //                     //                 let nonStandardBenefitDefintion = newNonStandard.fields.find(field => field.configRef.id === "ff72f3f2-d38d-ec11-b400-0022480a9a56");
    //                     //                 let nonStandardPayAccordingToBenefit = newNonStandard.fields.find(field => field.configRef.id === "fd72f3f2-d38d-ec11-b400-0022480a9a56");

    //                     //                 let templateDefintion = fieldMap["1fe66c74-710c-ef11-9f89-6045bdeb64f8"];

    //                     //                 const benefitDefinitionValue = templateDefintion!.values[0].value; //template value
    //                     //                 nonStandardBenefitDefintion!.values[0].value = structuredClone(benefitDefinitionValue); //ns value

    //                     //                 if (nonStandardPayAccordingToBenefit) {
    //                     //                     let templatePayAccordingToBenefit = fieldMap["05beb613-720c-ef11-9f89-6045bdeb64f8"];
    //                     //                     const payAccordingToBenefitValue = templatePayAccordingToBenefit.values[0].value; //template value
    //                     //                     nonStandardPayAccordingToBenefit!.values[0].value = structuredClone(payAccordingToBenefitValue); //ns value
    //                     //                 }
                                        

    //                     //                 dispatch(addUpdateNonStandards([{
    //                     //                     config: props.state.data.config,
    //                     //                     nonStandards: [newNonStandard],
    //                     //                     benefit: benefit,
    //                     //                     //groupConfiguration: props.groupConfiguration,
    //                     //                     changeTracker: {}
    //                     //                 }]));
    //                     //                 // Find or create the section within the category
    //                     //                 let section = cat.sectionRefs.find(sec => sec.sectionId === pick.item.nonStandard.sectionRef?.id);

    //                     //                 if (section) {
    //                     //                     // Section exists, add the non-standard to it
    //                     //                     section.nonstandards = [...section.nonstandards, { id: newNonStandard.id, name: newNonStandard.name }];
    //                     //                 } else {
    //                     //                     // Section doesn't exist, create it and add the non-standard
    //                     //                     const newSectionRef = {
    //                     //                         sectionId: pick.item.nonStandard.sectionRef?.id || '',
    //                     //                         nonstandards: [{ id: clonedNS.id, name: clonedNS.name }]
    //                     //                     };
    //                     //                     cat.sectionRefs = [...(cat.sectionRefs || []), newSectionRef];
    //                     //                 }
    //                     //             }
    //                     //         }
    //                     //     });
    //                     //     // update the intial NS to be considered Templated.
    //                     //     const updatedNonStandard: FAM.INonStandard = { ...pick.item.nonStandard, partOfTemplate: true };
    //                     //     updatedNonStandard.name = template.name;

    //                     //     dispatch(addUpdateNonStandards([{
    //                     //         config: props.state.data.config,
    //                     //         nonStandards: [updatedNonStandard],
    //                     //         benefit: props.benefit,
    //                     //         //groupConfiguration: props.groupConfiguration,
    //                     //         changeTracker: {}
    //                     //     }]));
                        

    //                     //     template.fields.forEach(templateField => {

    //                     //         const field = fieldMap[templateField.configRef.id];

    //                     //         // did we find a matching field
    //                     //         if (field) {
    //                     //             if (field.values.length === 0) {
    //                     //                 // nop
    //                     //             }
    //                     //             else if (field.values.length === 1 && templateField.values.length === 1) {

    //                     //                 const value = field.values[0].value;
    //                     //                 templateField.values[0].value = structuredClone(value);

    //                     //             } else if (field.values.length > 1) {
    //                     //                 let predicate = (oldValue: FAM.IFlexibleFieldValue, newValue: FAM.IFlexibleFieldValue) => {
    //                     //                     return oldValue.type === newValue.type;
    //                     //                 }
    //                     //                 if (tierChanges.length > 0) {
    //                     //                     predicate = (oldValue: FAM.IFlexibleFieldValue, newValue: FAM.IFlexibleFieldValue) => {
    //                     //                         return !!tierChanges.find(q => q.oldTier?.id === oldValue.tierRef?.id && q.newTier?.id === newValue.tierRef?.id)
    //                     //                     };
    //                     //                 }

    //                     //                 templateField.values.forEach(t => {

    //                     //                     const value = field.values.find(q => predicate(q, t));

    //                     //                     if (value?.value) {
    //                     //                         t.value = structuredClone(value.value);
    //                     //                     }
    //                     //                 });
    //                     //             }
    //                     //         }
    //                     //     });

    //                     //     const updatedTemplate = {
    //                     //         ...template,
    //                     //         relationships: newRelationshipList
    //                     //     };
    //                     //     nonStandardTemplates.push(updatedTemplate);
    //                     // });


    //                     // if (nonStandardTemplates && nonStandardTemplates.length > 0) {

    //                     //     dispatch(
    //                     //         addUpdateNonStandardTemplates([
    //                     //             {
    //                     //                 nonStandardTemplates: nonStandardTemplates
    //                     //             }
    //                     //         ])
    //                     //     );
    //                     //     updateAllTemplatedNonStandardsFields(nonStandardTemplates[0]);
    //                     // }

    //                     props.onDismiss?.();
    //                 }}
    //                 onCancel={() => {
    //                     setSourceContext(q => {
    //                         return {
    //                             ...q,
    //                             nonStandardSearchResult: null,
    //                             scopeSearchResult: null,
    //                         }
    //                     });
    //                     setNonStandardLinkIsOpen(false);
    //                 }}
    //                 onDismiss={() => {
    //                     setSourceContext(q => {
    //                         return {
    //                             ...q,
    //                             nonStandardSearchResult: null,
    //                             scopeSearchResult: null,
    //                         }
    //                     });
    //                     setNonStandardLinkIsOpen(false);
    //                 }}
    //             />

    //             </Stack>
    //     </div>
        
    // }

    /**
     * Renders an information callout with audit data.
     * @param auditData - The audit data to display in the callout.
     * @returns The rendered InformationCallout component.
     */
    const onRenderInformationCallout = (auditData?: IGridItem<INonstandardSearchResult>) => {

        if (!auditData?.data?.item.nonStandard)
            return <></>;

        const nonStandard = auditData?.data?.item.nonStandard;

        const benefit = auditData?.data?.item.type === ENonstandardScope.Benefit
            ? auditData?.data?.item.parent as FAM_IBenefit
            : undefined;

        return <InformationCallout
            hover
            postText={auditData.compareKeys}
            directionalHint={DirectionalHint.rightCenter}
            calloutClassName={appStyles.calloutView}
        >
            <div style={{ margin: '10px' }}>
                <NonStandardEditor
                    variant={EVariant.viewer}
                    isOpen={true}
                    defaultValue={nonStandard ?? null}
                    benefit={benefit}
                    disabled={true}
                />

            </div>
        </InformationCallout>;
    }

    const onRenderValidationDialog = () => {
        if (!alertIsMounted)
            return <></>;

        return <ConfirmationDialog
            onContinue={() => {
                setAlertIsOpen(false); setAlertIsMounted(false);
                setTimeout(()=>{
                    SaveAsync(
                        dispatch,
                        dataSource,
                        props.state,
                        {saveType: ESaveType.EAuto});
                },100)
            }}
            onClose={() => { setAlertIsOpen(false); setAlertIsMounted(false); }}
            continueText="Close"
            //cancelText="Close"
        >
            <Text>
            <p>Non-Standards have been updated or added as a result of this template action. </p>
            <p>You can open then NSE extract to view all Non-Standards and their changes.</p>
            </Text>
        </ConfirmationDialog>;
    }

    const onRenderFooter: IRenderFunction<IPanelProps> = () => {
        return (
            <>
                <PrimaryButton text='Continue'
                    styles={panelButtonsStyles}
                    disabled={props.disabled}
                    onClick={async () => {
                        //this is specific to the editing of the fields
                        updateAllTemplatedNonStandardsFields(nonStandardTemplate!);

                        setSelfVisible(false);

                        setTimeout(() => {
                            if (nonStandardTemplate)
                                onChange(nonStandardTemplate, changeTracker);
                        }, 300);

                        //TODO: if we deactivate this we need to deactivate all nonstandards related to the template
                        if (nonStandardTemplate?.deactivated === true) {
                            DeactivateTemplate()
                        }else if(wasPreviouslyDeactivated && nonStandardTemplate?.deactivated === false)
                        {
                            ReactivateTemplate()
                        }
                    }}
                />
                <DefaultButton text='Cancel'
                    styles={panelButtonsStyles}
                    onClick={() => {
                        props?.onDismiss?.();
                    }}
                />
            </>
        );
    }

    function DeactivateTemplate() {
        if (nonStandardTemplate?.relationships && nonStandardTemplate.relationships.length > 0) {
            let nonstandardPayloads: INonStandardPayload[] = []
            nonStandardTemplate.relationships.forEach(category => {
                let categoryID = category.categoryID;
                category.sectionRefs?.forEach(section => {
                    section.nonStandards?.forEach(templateNS => {
                        const templateNSId = templateNS.id;
                        //check group configuration 
                        if (props.state.data.json.id === categoryID)
                        {
                            //deactivate GC nonstandards
                            const foundNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === templateNSId);
                            if (foundNonStandard) {
                                const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                updatedNonStandard.deactivated = true;

                                const nonStandardPayload: INonStandardPayload = {
                                    config: props.state.data.config,
                                    nonStandards: [updatedNonStandard],
                                    //benefit: props.benefit,
                                    groupConfiguration: props.state.data.json,
                                    changeTracker: {}
                                }
                                nonstandardPayloads.push(nonStandardPayload)
                            }
                        }
                        //check benefits
                        const benefit = props.state.data.json.benefits.find(ben => ben.id === categoryID);
                        if (benefit)
                        {
                            //deactivate nonstandards
                            const foundNonStandard = benefit.nonStandards.find(ns => ns.id === templateNSId);
                            if (foundNonStandard) {
                                const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                updatedNonStandard.deactivated = true;
                                const nonStandardPayload: INonStandardPayload = {
                                    config: props.state.data.config,
                                    nonStandards: [updatedNonStandard],
                                    benefit: benefit,
                                    //groupConfiguration: props.groupConfiguration,
                                    changeTracker: {}
                                }
                                nonstandardPayloads.push(nonStandardPayload)
                            }
                        }
                    })
                })
            });
            if(nonstandardPayloads.length > 0)
                dispatch(addUpdateNonStandards(nonstandardPayloads))
        }
    }

    function ReactivateTemplate() {
        if (nonStandardTemplate?.relationships && nonStandardTemplate.relationships.length > 0) {
            let nonstandardPayloads: INonStandardPayload[] = []
            nonStandardTemplate.relationships.forEach(category => {
                let categoryID = category.categoryID;
                category.sectionRefs?.forEach(section => {
                    section.nonStandards?.forEach(templateNS => {
                        const templateNSId = templateNS.id;
                        //check group configuration 
                        if (props.state.data.json.id === categoryID)
                        {
                            //deactivate GC nonstandards
                            const foundNonStandard = props.state.data.json.nonStandards.find(ns => ns.id === templateNSId);
                            if (foundNonStandard) {
                                const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                updatedNonStandard.deactivated = false;

                                const nonStandardPayload: INonStandardPayload = {
                                    config: props.state.data.config,
                                    nonStandards: [updatedNonStandard],
                                    //benefit: props.benefit,
                                    groupConfiguration: props.state.data.json,
                                    changeTracker: {}
                                }
                                nonstandardPayloads.push(nonStandardPayload)
                            }
                        }
                        //check benefits
                        const benefit = props.state.data.json.benefits.find(ben => ben.id === categoryID);
                        if (benefit)
                        {
                            //deactivate nonstandards
                            const foundNonStandard = benefit.nonStandards.find(ns => ns.id === templateNSId);
                            if (foundNonStandard) {
                                const updatedNonStandard: FAM_INonStandard = structuredClone(foundNonStandard);
                                updatedNonStandard.deactivated = false;
                                const nonStandardPayload: INonStandardPayload = {
                                    config: props.state.data.config,
                                    nonStandards: [updatedNonStandard],
                                    benefit: benefit,
                                    //groupConfiguration: props.groupConfiguration,
                                    changeTracker: {}
                                }
                                nonstandardPayloads.push(nonStandardPayload)
                            }
                        }
                    })
                })
            });
            if(nonstandardPayloads.length > 0)
                dispatch(addUpdateNonStandards(nonstandardPayloads))
        }
    }

    function onRenderCategoryTitle(): JSX.Element {
        const linkedConfigCategory = props.state.data.config.cache.map_bcbs_config_category[props.defaultValue?.linkedCategoryTypeId!];

        if (!linkedConfigCategory?.bcbs_name) {
            return <></>
        }

        return <TextFieldControl
            disabled={true}
            label={"Category Type"}
            defaultFieldValue={{ type: 'string', value: linkedConfigCategory.bcbs_name ?? '' }}
        />
    }

    if (props.variant === EVariant.viewer)
        return onRenderBody();

    return (
        <Panel
            isOpen={props.isOpen && selfVisible}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            headerText="Non-Standard Template"
            isLightDismiss
            onRenderFooterContent={onRenderFooter}
            isFooterAtBottom={true}
            >
            <div>
                {onRenderValidationDialog()}
                {/* {onRenderLinkNonStandards()} */}
                {onRenderCategoryTitle()}
                {onRenderBody()}
            </div>
            </Panel> 
    )

}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(NonStandardTemplateEditor)




